import type {
  ImpactPackage,
  ImpactPackagePage,
  ImpactPackageEstimateBasis,
  ImpactPackagePeriodChange,
  ImpactPackageSearchConsoleClicks,
  ImpactPackageSearchConsoleUnmeasured
} from './impact-brief-types';

/**
 * The review queue — the Impact Brief's packages flattened into the order an
 * operator actually works them, one URL at a time.
 *
 * This exists because the packages strip and the review loop want opposite
 * shapes of the same data. The strip is a *map*: four packages, five pages
 * each, grouped so you can see where the week's damage is concentrated. The
 * loop is a *belt*: forty-five URLs, one decision each, and the grouping is
 * context rather than structure.
 *
 * Nothing here re-ranks anything. The composer already ordered the packages and
 * the pages inside them (IMPACT_BRIEF_PLAN.md, locked decision 1), so the belt
 * is simply that order read end to end. Flattening is the only transformation,
 * and de-duplication is the only judgement — see `buildReviewQueue`.
 */

export interface ReviewQueueEntry {
  urlId: string;
  url: string;
  /** Server-precomputed pathname. Never derived here. */
  path: string;
  keyword: string | null;
  /**
   * Where the keyword came from, in a workspace whose Search Console picks it (GSC_INTEGRATION.md
   * §4.4). Absent everywhere else: a workspace without GSC shows no source line at all (§1a).
   */
  searchConsoleLine?: string;
  /** The page's uncaptured demand, for the claimed-gap running total. */
  gap: number | null;
  /** Null when the page did not move this period. */
  periodChange: ImpactPackagePeriodChange | null;
  /** Shown beside `periodChange`, never merged into it; absent when Search Console is not on. */
  searchConsoleClicks?: ImpactPackageSearchConsoleClicks;
  /** Why Search Console could not measure the period; absent when it is not on or it could. */
  searchConsoleUnmeasured?: ImpactPackageSearchConsoleUnmeasured;
  /** What the estimate in `periodChange` rests on; absent where Search Console is on. */
  estimateBasis?: ImpactPackageEstimateBasis;
  /** The package this URL is reviewed under — the first one that claims it. */
  packageId: string;
  /**
   * The page's own title (`page.title`), falling back to the package's. The
   * belt shows one page at a time under this, so a package title here reads as
   * a verdict on that page (§0bf).
   */
  packageTitle: string;
  /** What the page did, when the package title says otherwise (§0bf). */
  standing: string | null;
  /**
   * How many OTHER packages also list this URL.
   *
   * Kept as a count rather than dropped, because it is the answer to the
   * question the de-duplication provokes: the strip says 45 pages and the belt
   * says 43, and this is where the missing two went.
   */
  alsoInPackages: number;
}

export interface BuildReviewQueueOptions {
  /**
   * URLs already triaged away — `PlanV2IssueQueueService.ignoredUrlIds()`.
   *
   * Filtered at build time rather than skipped during the walk: a URL the
   * operator ignored last session is not a decision they still owe, and having
   * it sit in the belt as an item to be pressed past is exactly the friction
   * the loop exists to remove.
   */
  skipUrlIds?: Iterable<string>;
}

/**
 * Packages in, one ordered list of URLs out.
 *
 * Two rules, both of which cost something and both of which are deliberate:
 *
 * **Link packages are excluded.** A link package's rows are anchor suggestions
 * between two pages, not pages with issues to dispose of; there is no
 * `app-url-detail` to render for a row that names two URLs and a phrase. They
 * stay on the strip, where they read fine. The belt keeps its size by reading the fill packages
 * after the slots (`reviewBeltPackages`, §39), so pass it those, not the slots alone.
 *
 * **A URL appears once.** The strip deliberately lets the same page sit in two
 * packages — a decay prefix and a cluster can honestly both claim it, and
 * `impact-packages` keys its open row by package *and* url for exactly that
 * reason. A belt cannot: presenting the same URL twice asks the operator to
 * make the same decision twice, and the second time they have no way to tell
 * whether they already answered it. The first package to claim a URL keeps it,
 * which because packages arrive ranked means the strongest reason to look at
 * the page is the one framing it.
 */
export function buildReviewQueue(
  packages: readonly ImpactPackage[] | null | undefined,
  options: BuildReviewQueueOptions = {}
): ReviewQueueEntry[] {
  const skip = new Set(options.skipUrlIds ?? []);
  const byUrlId = new Map<string, ReviewQueueEntry>();
  const order: ReviewQueueEntry[] = [];

  for (const pkg of packages ?? []) {
    if (!pkg || pkg.kind === 'link') continue;
    for (const page of pkg.pages ?? []) {
      const entry = toEntry(pkg, page);
      if (!entry) continue;
      if (skip.has(entry.urlId)) continue;
      const seen = byUrlId.get(entry.urlId);
      if (seen) {
        seen.alsoInPackages += 1;
        continue;
      }
      byUrlId.set(entry.urlId, entry);
      order.push(entry);
    }
  }

  return order;
}

/**
 * The content packages `/review` reads after the slots: one for each link package that holds a slot,
 * the next-ranked content packages that did not get one (ISSUES_REVIEW_FINDINGS §39).
 *
 * The belt has no page to open for a link package, so a link package that wins a slot used to take
 * a package's worth of pages off `/review`: on ManyRequests, 41 pages fell to 24 when the internal
 * links took the fourth slot. The owner's rule (2026-11-03): the slots stay as ranked, and the belt
 * fills from the next content package, so it keeps as many content packages as there are slots.
 *
 * `ranked` is every candidate in rank order, slots included. Fewer come back when the candidates
 * run out. The Dashboard lists these packages' pages below its slots (§0ck): a page on `/review`
 * has a line there.
 */
export function reviewFillPackages<T extends Pick<ImpactPackage, 'id' | 'kind'>>(
  slots: readonly T[],
  ranked: readonly T[]
): T[] {
  const linkSlots = slots.filter((pkg) => pkg.kind === 'link').length;
  if (linkSlots === 0) return [];
  const slotted = new Set(slots.map((pkg) => pkg.id));
  return ranked.filter((pkg) => pkg.kind !== 'link' && !slotted.has(pkg.id)).slice(0, linkSlots);
}

/**
 * The packages the belt walks, in order: the slots, then the fill (`reviewFillPackages`). Every
 * reader of the belt goes through this, so `/review`, the agent export and the Review button's count
 * read one list.
 */
export function reviewBeltPackages<T>(brief: {
  packages?: readonly T[] | null;
  reviewFillPackages?: readonly T[] | null;
} | null | undefined): T[] {
  return [...(brief?.packages ?? []), ...(brief?.reviewFillPackages ?? [])];
}

/**
 * A page row becomes a belt item, or it does not become one at all.
 *
 * A row with no `urlId` cannot be dispositioned — every queue operation is
 * keyed by it — and a row with no `url` has nothing for `app-url-detail` to
 * load. Either one would render as an item the operator can look at and cannot
 * act on, so neither is admitted.
 */
function toEntry(pkg: ImpactPackage, page: ImpactPackagePage): ReviewQueueEntry | null {
  const urlId = (page?.urlId || '').trim();
  const url = (page?.url || '').trim();
  if (!urlId || !url) return null;
  return {
    urlId,
    url,
    path: (page.path || '').trim(),
    keyword: (page.keyword || '').trim() || null,
    ...(page.searchConsoleLine ? { searchConsoleLine: page.searchConsoleLine } : {}),
    gap: page.gap,
    periodChange: page.periodChange ?? null,
    ...(page.searchConsoleClicks ? { searchConsoleClicks: page.searchConsoleClicks } : {}),
    ...(page.searchConsoleUnmeasured ? { searchConsoleUnmeasured: page.searchConsoleUnmeasured } : {}),
    ...(page.estimateBasis ? { estimateBasis: page.estimateBasis } : {}),
    packageId: pkg.id,
    packageTitle: (page.title || '').trim() || pkg.title,
    standing: (page.standing || '').trim() || null,
    alsoInPackages: 0
  };
}

/**
 * Where in the belt the operator is, phrased for the progress rail.
 *
 * `position` is 1-based because it is read by a person; `index` everywhere else
 * in this module is 0-based because it indexes an array. Conflating the two is
 * how a queue reports "0 of 45" on its first item.
 *
 * An empty queue is `0 of 0` at 100%, not 0%: a bar that sits empty says work
 * is waiting, and for a finished — or never-populated — queue the truthful
 * reading is that there is none.
 */
export function reviewProgress(
  total: number,
  index: number
): { position: number; total: number; percent: number } {
  const size = Math.max(0, Math.floor(total));
  if (size === 0) return { position: 0, total: 0, percent: 100 };
  const clamped = Math.min(Math.max(0, Math.floor(index)), size - 1);
  return {
    position: clamped + 1,
    total: size,
    percent: Math.round(((clamped + 1) / size) * 100)
  };
}

/**
 * Step through the belt without falling off either end.
 *
 * Clamping rather than wrapping. A queue that loops back to item one when you
 * press past the last one hides the only event the loop is built to deliver —
 * reaching the end — and turns "am I done?" into a question the operator has to
 * answer by recognising a URL they saw four minutes ago.
 */
export function nextReviewIndex(total: number, index: number, step: number): number {
  const size = Math.max(0, Math.floor(total));
  if (size === 0) return 0;
  return Math.min(Math.max(0, Math.floor(index) + Math.floor(step)), size - 1);
}

/**
 * The URLs that gained a work item since the loop started.
 *
 * Computed as a set difference against a snapshot taken on entry rather than by
 * counting the operator's own clicks. The dispositions are written by the issue
 * cards inside `app-url-detail`, several components below the loop, and one
 * URL can produce several work items from one visit (a technical card and a
 * section gap, say). Watching the queue service's own records means the loop
 * counts what was actually persisted, and cannot drift from it.
 */
export function newlyClaimedUrlIds(
  before: ReadonlySet<string>,
  after: ReadonlySet<string>
): Set<string> {
  const gained = new Set<string>();
  for (const urlId of after) {
    if (!before.has(urlId)) gained.add(urlId);
  }
  return gained;
}

/**
 * Uncaptured demand across a set of URLs — the number the loop counts up.
 *
 * The point of the running total is that clearing a queue should read as
 * getting something rather than as emptying something, and `gap` is the amount
 * already on screen everywhere else in the brief ("gap 3,491", "uncaptured
 * 5,868"). Same field, same meaning, so the loop's total and the strip's header
 * are the same currency.
 *
 * Pages with a null gap contribute nothing rather than breaking the sum: a page
 * can be worth reviewing on decay alone, with no demand figure attached.
 */
export function claimedGap(
  queue: readonly ReviewQueueEntry[],
  urlIds: ReadonlySet<string>
): number {
  let sum = 0;
  for (const entry of queue) {
    if (!urlIds.has(entry.urlId)) continue;
    if (Number.isFinite(entry.gap as number)) sum += entry.gap as number;
  }
  return sum;
}

/**
 * "3 dismissed · 7 sent to work items · 12 of 45 seen".
 *
 * Three numbers rather than one, because they answer different questions and a
 * single "12 done" would have to pick which one it meant. `seen` counts every
 * item the operator advanced past, decided or not — skipping is a legitimate
 * outcome and a summary that omits it under-reports the work.
 */
export function reviewOutcome(
  dismissed: ReadonlySet<string>,
  claimed: ReadonlySet<string>,
  seen: ReadonlySet<string>
): { dismissed: number; claimed: number; seen: number; decided: number } {
  const decided = new Set<string>([...dismissed, ...claimed]);
  return {
    dismissed: dismissed.size,
    claimed: claimed.size,
    seen: seen.size,
    decided: decided.size
  };
}

/** The metric label the crawl diff gives estimated organic traffic (`crawl-run-diff.ts`). */
export const ESTIMATED_TRAFFIC_METRIC_LABEL = 'Est. organic traffic';

/**
 * The metric label of a period change ranked on Search Console clicks: where it is on, a page lost
 * or gained traffic only when its clicks moved materially (owner, 2026-11-03,
 * TRAFFIC_ESTIMATE_CUTOVER.md D2; `crawl-run-movers.ts`).
 */
export const SEARCH_CONSOLE_CLICKS_METRIC_LABEL = 'Search Console clicks';

/**
 * Whether the page header shows the period's change from our crawl.
 *
 * Where Search Console is on, its clicks replace our crawl's traffic estimate (owner, 2026-10-20;
 * TECHNICAL_DEBT §0cd): graded 2026-10-20, the estimate ran 2.5–10× the clicks on the same pages, so
 * printed beside them it was wrong advice, labelled or not. That holds when the clicks cannot
 * compare the period too: the failure is said once for the period (`reviewPeriodSearchConsoleNote`),
 * not filled in with the estimate. A SERP-position change is not an estimate of traffic and is still shown.
 * Without Search Console nothing here changes.
 */
export function reviewShowsPeriodChange(entry: PeriodChangeSources): boolean {
  const change = entry.periodChange;
  if (!change) return false;
  // The page's own clicks line (`searchConsoleClicks`) already says this change; printed again it
  // would read as a second move.
  if (change.metricLabel === SEARCH_CONSOLE_CLICKS_METRIC_LABEL) return false;
  if (change.metricLabel !== ESTIMATED_TRAFFIC_METRIC_LABEL) return true;
  return !searchConsoleIsOn(entry);
}


/** What a page (a `/review` entry or a Dashboard package page) says moved, from both sources. */
export type PeriodChangeSources = Pick<ReviewQueueEntry, 'periodChange' | 'searchConsoleClicks' | 'searchConsoleUnmeasured'>;

/**
 * Search Console is on for this page's workspace: it either measured the period or said why it
 * could not. Either way its clicks, not our crawl's estimate, are the traffic number.
 */
export function searchConsoleIsOn(
  sources: Pick<PeriodChangeSources, 'searchConsoleClicks' | 'searchConsoleUnmeasured'>
): boolean {
  return !!sources.searchConsoleClicks || !!sources.searchConsoleUnmeasured;
}

/**
 * The clicks one page lost this period, for the Dashboard's "clicks lost" (package and brief
 * totals). Only a material loss counts (`gsc-click-movement.ts`): a change the material test calls
 * chance is not a loss a writer should be told about, so it adds nothing, as `/review` colours it
 * as nothing. 0 without Search Console.
 *
 * The Dashboard follows `/review` here (owner, 2026-11-03, TRAFFIC_ESTIMATE_CUTOVER.md D1;
 * TECHNICAL_DEBT §0cu, §0ck): where Search Console is on, the estimate is not shown as movement.
 * On 1 November DataForSEO changed how it estimates traffic, and every site's estimate fell 2–15×
 * with clicks flat; the Dashboard printed that as "−2500".
 */
export function searchConsoleClicksLost(page: Pick<PeriodChangeSources, 'searchConsoleClicks'>): number {
  const clicks = page.searchConsoleClicks;
  return clicks && clicks.material && clicks.change < 0 ? -clicks.change : 0;
}

/** What the Dashboard shows moved on one page. Both null: nothing to say. */
export interface DashboardPageMovement {
  /** The page's Search Console click change, when it is material. */
  clicks: ImpactPackageSearchConsoleClicks | null;
  /** Our crawl's change, when `/review` shows it too (`reviewShowsPeriodChange`). */
  periodChange: ImpactPackagePeriodChange | null;
}

/**
 * The page's movement as the Dashboard states it. Where Search Console is on: its click change if
 * material, and a SERP-position change, never the estimate; a click change that is not material,
 * or a period it could not measure, says nothing on the page (the period's reason is said once,
 * above). Without Search Console: our crawl's change, exactly as before.
 */
export function dashboardPageMovement(page: PeriodChangeSources): DashboardPageMovement {
  const clicks = page.searchConsoleClicks?.material ? page.searchConsoleClicks : null;
  return { clicks, periodChange: reviewShowsPeriodChange(page) ? page.periodChange : null };
}

/**
 * The traffic a package, or the whole brief, lost this period, as the Dashboard states it.
 * `searchConsoleClicksLost` is present exactly where Search Console is on: then the loss is in its
 * clicks, null when it could not measure the period, and our estimate's loss is not shown, however
 * large. Absent: our estimate's loss, as before.
 */
export function dashboardTrafficLost(
  estimateLost: number | null,
  searchConsoleClicksLost: number | null | undefined
): { source: 'search-console' | 'estimate'; lost: number | null } {
  return searchConsoleClicksLost !== undefined
    ? { source: 'search-console', lost: searchConsoleClicksLost }
    : { source: 'estimate', lost: estimateLost };
}

/** One tile of the Dashboard's header strip, already worded. */
export interface DashboardHeaderTile {
  label: string;
  valueDisplay: string;
  deltaDisplay: string;
  tone: 'change-up' | 'change-down' | 'change-flat';
}

/**
 * The header's traffic tile where Search Console is on: the property's clicks in the window before
 * the newer crawl, and their change from the window before the older one (`gscMovementWindows`).
 *
 * All pages of the property, not only the pages we crawled: it is the number the client sees in
 * Search Console, and the crawl misses pages that earn clicks (TRAFFIC_ESTIMATE_CUTOVER.md §3 stage
 * 1; the choice is argued in its PR). The label says "all pages" so it is not read as the crawled
 * pages' total.
 *
 * The delta is coloured only when it is material, by the same test as a page's: a site's clicks
 * move by chance too, and a red "−30" on a flat 900 is the false alarm this replaces.
 *
 * When the windows cannot compare, the tile says so and the reason is said once, under the strip
 * (`searchConsoleUnmeasuredSentence`). Never the estimate instead.
 */
export function dashboardSiteClicksTile(
  clicks: ImpactPackageSearchConsoleClicks | null | undefined,
  unmeasured: ImpactPackageSearchConsoleUnmeasured | null | undefined
): DashboardHeaderTile | null {
  if (clicks) {
    const change = clicks.change;
    const magnitude = Math.abs(change).toLocaleString('en-US');
    return {
      label:
        clicks.windowKind === 'month'
          ? 'Search Console clicks, all pages, month before crawl'
          : `Search Console clicks, all pages, ${clicks.windowDays} days before crawl`,
      valueDisplay: clicks.current.clicks.toLocaleString('en-US'),
      deltaDisplay: change === 0 ? 'no change' : `${change > 0 ? '+' : '-'}${magnitude}`,
      tone: !clicks.material || change === 0 ? 'change-flat' : change > 0 ? 'change-up' : 'change-down'
    };
  }
  if (unmeasured) {
    return {
      label: 'Search Console clicks, all pages',
      valueDisplay: '—',
      deltaDisplay: 'not measured this period',
      tone: 'change-flat'
    };
  }
  return null;
}

const DAY_MS = 86_400_000;

function dayCount(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
}

function addDays(day: string, n: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
}

/** `YYYY-MM` of the calendar month before the one `day` is in: the month Search Console compares. */
function monthBefore(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 7);
}

/**
 * The fewest days between two crawls that Search Console can compare: its windows are whole weeks
 * before each crawl and never overlap, so the shortest is one week. `gsc-click-movement.ts` imports
 * it; below it, it returns `crawls_under_a_week_apart`.
 */
export const SEARCH_CONSOLE_MIN_DAYS_BETWEEN_CRAWLS = 7;

/**
 * The failed measurement, said once for the period, on `/review` and in both exports alike. Worded
 * as a measurement that could not be made, never as "nothing moved"; for every page, so no page reads
 * as measured; with where the stored data ends; and, when the stored data can tell, when it will be
 * possible again.
 */
export function searchConsoleUnmeasuredSentence(unmeasured: ImpactPackageSearchConsoleUnmeasured): string {
  const { previousCrawlCapturedOn: a, currentCrawlCapturedOn: b, dataThrough } = unmeasured;
  const lead = "Search Console can't measure what moved on any page this period";
  const ends = dataThrough ? ` Its stored data ends on ${dataThrough}.` : '';
  if (unmeasured.reason === 'not_synced') {
    return `${lead}: the site's Search Console data has not finished its first sync. It can once that sync finishes.`;
  }
  if (unmeasured.reason === 'crawls_under_a_week_apart') {
    const days = dayCount(a, b);
    return (
      `${lead}: it compares whole weeks of clicks before each crawl, and this period's two crawls (${a} and ${b}) are ${days} ${days === 1 ? 'day' : 'days'} apart.` +
      `${ends} The next crawl can be measured against the ${b} one if it is captured on or after ${addDays(b, SEARCH_CONSOLE_MIN_DAYS_BETWEEN_CRAWLS)}.`
    );
  }
  // `not_stored`: the older crawl's weeks are kept only as monthly totals, so the months are compared.
  const previousMonth = monthBefore(a);
  const currentMonth = monthBefore(b);
  return previousMonth === currentMonth
    ? `${lead}: the days before the older crawl (${a}) are kept only as monthly totals, and both crawls (${a} and ${b}) fall after the same month, ${currentMonth}, so there are no two months to compare.${ends}`
    : `${lead}: the days before the older crawl (${a}) are kept only as monthly totals, and the month before the newer crawl (${b}), ${currentMonth}, is not complete in the stored data.${ends} It can once the data for ${currentMonth} is stored in full.`;
}

/**
 * The line `/review` shows once for the period, above the page, where Search Console could not
 * measure it: the reason is the same for every page, so a per-page note was noise (graded
 * 2026-10-21), and one on some pages only read as if the others had been measured. Null otherwise.
 */
export function reviewPeriodSearchConsoleNote(queue: readonly ReviewQueueEntry[]): string | null {
  const unmeasured = queue.find((e) => e.searchConsoleUnmeasured)?.searchConsoleUnmeasured;
  return unmeasured ? searchConsoleUnmeasuredSentence(unmeasured) : null;
}
